import { useMemo, useState } from "react";
import { Calculator, TrendingUp, Euro, Target } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid } from "recharts";
import { useLocale } from "@/context/LocaleContext";

export default function ProfitCalculator() {
    const { tr, fmtPrice, currencySymbol, rate } = useLocale();
    const [units, setUnits] = useState(50);
    const [price, setPrice] = useState(3.5);
    const [cost, setCost] = useState(1.5);
    const [weekDays, setWeekDays] = useState(5);
    const [model, setModel] = useState("alfa");

    const investment = model === "alfa" ? 2000 : 3000;

    const result = useMemo(() => {
        const dailyRev = units * price;
        const dailyCost = units * cost;
        const dailyProfit = dailyRev - dailyCost;
        const weeklyProfit = dailyProfit * weekDays;
        const monthlyProfit = weeklyProfit * 4;
        const margin = dailyRev > 0 ? (dailyProfit / dailyRev) * 100 : 0;
        const projection = Array.from({ length: 13 }, (_, m) => ({
            month: m,
            cumulative: Math.round(monthlyProfit * m),
        }));
        const breakEven = monthlyProfit > 0 ? Math.ceil(investment / monthlyProfit) : null;
        const roi12 = investment > 0 ? ((monthlyProfit * 12 - investment) / investment) * 100 : 0;
        return { dailyRev, dailyCost, dailyProfit, weeklyProfit, monthlyProfit, margin, projection, breakEven, roi12 };
    }, [units, price, cost, weekDays, investment]);

    const fmt = (v) => fmtPrice(v, { decimals: 0 });

    const inputs = [
        { key: "units", label: tr("Unidades vendidas por dia", "Units sold per day"), value: units, set: setUnits, step: 1, min: 1, max: 500, suffix: "un" },
        { key: "price", label: tr("Preço médio por unidade", "Average price per unit"), value: price, set: setPrice, step: 0.1, min: 0.1, max: 50, suffix: "€" },
        { key: "cost", label: tr("Custo médio por unidade", "Average cost per unit"), value: cost, set: setCost, step: 0.1, min: 0, max: 50, suffix: "€" },
        { key: "weekDays", label: tr("Dias de trabalho por semana", "Working days per week"), value: weekDays, set: setWeekDays, step: 1, min: 1, max: 7, suffix: tr("dias", "days") },
    ];

    return (
        <section id="calculadora" className="relative py-12 lg:py-16 bg-primary text-primary-foreground overflow-hidden" data-testid="profit-calculator-section">
            <div className="absolute inset-0 grain-overlay opacity-40" />
            <div
                aria-hidden
                className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full opacity-30"
                style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }}
            />
            <div className="relative container-x">
                <div className="max-w-2xl">
                    <span className="eyebrow text-secondary">{tr("Lucro e retorno", "Profit & ROI")}</span>
                    <h2 className="mt-4 font-heading text-4xl sm:text-3xl sm:text-4xl lg:text-5xl lg:text-6xl font-extrabold leading-[1.02]">
                        {tr("Calcule o seu ", "Calculate your profit ")}<span className="text-secondary">{tr("potencial", "potential")}</span>{tr(" de lucro.", ".")}
                    </h2>
                    <p className="mt-5 text-lg text-white/75 max-w-xl">
                        {tr(
                            "Descubra, em segundos, quanto pode ganhar com um carrinho NOVIFOOD — e em quantos meses recupera o investimento.",
                            "Find out, in seconds, how much you can earn with a NOVIFOOD cart — and how many months until you recover your investment."
                        )}
                    </p>
                </div>

                <div className="mt-14 grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-7 rounded-2xl bg-white/5 border border-white/10 p-6 sm:p-8 backdrop-blur">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-2 text-secondary">
                                <Calculator className="h-4 w-4" />
                                <span className="eyebrow text-secondary">{tr("Parâmetros", "Parameters")}</span>
                            </div>
                            <div className="flex items-center gap-1 rounded-full bg-white/10 p-1" data-testid="roi-model-toggle">
                                {[
                                    { id: "alfa", label: `Alfa · ${fmtPrice(2000)}` },
                                    { id: "omega", label: `Ômega · ${fmtPrice(3000)}` },
                                ].map((m) => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        data-testid={`roi-model-${m.id}`}
                                        onClick={() => setModel(m.id)}
                                        className={`rounded-full px-3 py-1 text-xs font-bold transition ${
                                            model === m.id ? "bg-secondary text-primary" : "text-white/70 hover:text-white"
                                        }`}
                                    >
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 grid gap-6 sm:grid-cols-2">
                            {inputs.map((f) => (
                                <div key={f.key}>
                                    <label className="flex items-center justify-between text-sm font-medium text-white/80">
                                        <span>{f.label}</span>
                                        <span className="font-mono text-secondary">{f.value}{f.suffix === "€" ? "€" : ""} {f.suffix !== "€" && f.suffix !== "un" ? f.suffix : ""}</span>
                                    </label>
                                    <input
                                        type="range"
                                        min={f.min}
                                        max={f.max}
                                        step={f.step}
                                        value={f.value}
                                        onChange={(e) => f.set(parseFloat(e.target.value))}
                                        data-testid={`profit-calculator-input-${f.key}`}
                                        className="mt-3 w-full accent-yellow-400"
                                    />
                                    <input
                                        type="number"
                                        min={f.min}
                                        max={f.max}
                                        step={f.step}
                                        value={f.value}
                                        onChange={(e) => f.set(parseFloat(e.target.value) || 0)}
                                        data-testid={`profit-calculator-number-${f.key}`}
                                        className="mt-2 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-secondary"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* 12-month ROI projection */}
                        <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-4 sm:p-5" data-testid="roi-projection-chart">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2 text-secondary">
                                    <Target className="h-4 w-4" />
                                    <span className="text-xs font-bold uppercase tracking-[0.18em]">
                                        {tr("Projeção a 12 meses", "12-month projection")}
                                    </span>
                                </div>
                                {result.breakEven && result.breakEven <= 12 && (
                                    <span className="rounded-full bg-secondary px-3 py-1 text-[11px] font-extrabold text-primary" data-testid="roi-breakeven-badge">
                                        {tr(`Retorno no mês ${result.breakEven}`, `Break-even in month ${result.breakEven}`)}
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 h-52">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={result.projection} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="roiGold" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#E0AE32" stopOpacity={0.7} />
                                                <stop offset="100%" stopColor="#E0AE32" stopOpacity={0.05} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                        <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" fontSize={11}
                                            tickFormatter={(m) => (m === 0 ? "" : `M${m}`)} />
                                        <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11}
                                            tickFormatter={(v) => `${currencySymbol}${Math.round(v * rate / 1000)}k`} width={44} />
                                        <Tooltip
                                            formatter={(v) => [fmt(v), tr("Lucro acumulado", "Cumulative profit")]}
                                            labelFormatter={(m) => tr(`Mês ${m}`, `Month ${m}`)}
                                            contentStyle={{ background: "#0B2545", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#fff" }}
                                        />
                                        <ReferenceLine y={investment} stroke="#fff" strokeDasharray="6 4"
                                            label={{ value: tr("Investimento", "Investment"), fill: "rgba(255,255,255,0.7)", fontSize: 10, position: "insideTopRight" }} />
                                        <Area type="monotone" dataKey="cumulative" stroke="#E0AE32" strokeWidth={2.5} fill="url(#roiGold)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="rounded-2xl bg-secondary text-primary p-6 sm:p-8 shadow-xl" data-testid="profit-calculator-result">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4" />
                                <span className="eyebrow text-primary/70">{tr("Resultado estimado", "Estimated result")}</span>
                            </div>
                            <div className="mt-6 space-y-4">
                                <Row label={tr("Receita diária", "Daily revenue")} value={fmt(result.dailyRev)} />
                                <Row label={tr("Custo diário", "Daily cost")} value={fmt(result.dailyCost)} />
                                <Row label={tr("Lucro diário", "Daily profit")} value={fmt(result.dailyProfit)} emphasis />
                                <div className="my-4 stripe-divider h-1 rounded" />
                                <Row label={tr("Margem", "Margin")} value={`${result.margin.toFixed(1)}%`} />
                                <Row label={tr("Lucro semanal", "Weekly profit")} value={fmt(result.weeklyProfit)} />
                                <Row label={tr("Lucro mensal (× 4 semanas)", "Monthly profit (× 4 weeks)")} value={fmt(result.monthlyProfit)} big />
                                <div className="my-4 stripe-divider h-1 rounded" />
                                <Row label={tr("ROI a 12 meses", "12-month ROI")} value={`${result.roi12 > 0 ? "+" : ""}${result.roi12.toFixed(0)}%`} emphasis />
                                <Row
                                    label={tr("Recuperação do investimento", "Investment payback")}
                                    value={result.breakEven ? tr(`${result.breakEven} ${result.breakEven === 1 ? "mês" : "meses"}`, `${result.breakEven} month${result.breakEven === 1 ? "" : "s"}`) : "—"}
                                />
                            </div>
                            <div className="mt-8 flex items-center gap-3 rounded-lg bg-primary/10 p-4">
                                <Euro className="h-5 w-5" />
                                <p className="text-sm text-primary/80">
                                    {tr("Valores ilustrativos — baseados nos parâmetros introduzidos.", "Illustrative values — based on the parameters entered.")}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Row({ label, value, emphasis, big }) {
    return (
        <div className="flex items-baseline justify-between gap-4">
            <span className={`text-sm ${emphasis ? "font-semibold" : ""} text-primary/75`}>{label}</span>
            <span
                className={`font-heading font-extrabold tabular-nums ${
                    big ? "text-3xl" : emphasis ? "text-2xl" : "text-xl"
                }`}
            >
                {value}
            </span>
        </div>
    );
}
